import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { getContentByType, getContentFromCache, getTranslation, ContentDocument } from '../api/contentApi';

const GlossaryPage: React.FC = () => {
  const { t } = useTranslation();
  const { lang } = useParams<{ lang: string }>();
  const currentLang = lang || 'de';

  const [entries, setEntries] = useState<ContentDocument[]>(() => getContentFromCache('glossary') ?? []);
  const [loading, setLoading] = useState(() => getContentFromCache('glossary') === null);
  const [error, setError] = useState('');
  const [searchTerm, setSearchTerm] = useState('');

  useEffect(() => {
    getContentByType('glossary')
      .then(setEntries)
      .catch(() => setError(t('common.error')))
      .finally(() => setLoading(false));
  }, [t]);

  // Filter by language availability and search term
  const visibleEntries = entries
    .filter((entry) => {
      const tr = getTranslation(entry, currentLang);
      if (!tr) return false;
      if (searchTerm) {
        const term = (tr.term || '').toLowerCase();
        const explanation = (tr.explanation || '').toLowerCase();
        const query = searchTerm.toLowerCase();
        return term.includes(query) || explanation.includes(query);
      }
      return true;
    })
    .sort((a, b) => {
      const termA = (getTranslation(a, currentLang)?.term || '').toLowerCase();
      const termB = (getTranslation(b, currentLang)?.term || '').toLowerCase();
      return termA.localeCompare(termB);
    });

  if (loading) return <div className="container"><p>{t('common.loading')}</p></div>;
  if (error) return <div className="container"><div className="error">{error}</div></div>;

  return (
    <div>
      {/* Hero section — 100vh full bleed. TODO: replace with url('/images/glossary.jpg') */}
      <section
        className="hero-section"
        style={{ backgroundImage: `url(${process.env.PUBLIC_URL}/cd4d63_7c77e61da6b340baa7c7295fa3040632~mv2.avif)` }}
      >
        <div className="hero-overlay" />
        <div className="hero-content">
          <h1>{t('nav.glossary')}</h1>
        </div>
      </section>

      <div className="max-w-4xl mx-auto py-12 px-4">

      {/* Search */}
      <div className="mb-8">
        <input
          type="text"
          placeholder={t('common.search')}
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="w-full md:w-96 px-4 py-3 border border-gray-300 rounded-lg focus:border-primary-500 focus:ring-1 focus:ring-primary-500 outline-none"
        />
      </div>

      {visibleEntries.length === 0 ? (
        <p className="text-gray-500">
          {currentLang === 'de' ? 'Keine Einträge gefunden.' : currentLang === 'fr' ? 'Aucune entrée trouvée.' : 'No entries found.'}
        </p>
      ) : (
        <div className="space-y-6">
          {visibleEntries.map((entry) => {
            const tr = getTranslation(entry, currentLang)!;
            return (
              <div key={entry.id} id={`glossary-${entry.id}`} className="border-b border-gray-200 pb-6 scroll-mt-24">
                <h3 className="text-xl font-semibold text-primary-700 mb-2">{tr.term}</h3>
                {(entry.categories && entry.categories.length > 0
                  ? entry.categories
                  : entry.category ? [entry.category] : []
                ).map((cat) => (
                  <span key={cat} className="inline-block text-xs font-medium text-primary-600 bg-primary-50 px-2 py-1 rounded mb-2 mr-1">
                    {cat}
                  </span>
                ))}
                <div className="prose max-w-none text-gray-700" dangerouslySetInnerHTML={{ __html: tr.explanation || '' }} />
              </div>
            );
          })}
        </div>
      )}
      </div>
    </div>
  );
};

export default GlossaryPage;
